import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Modal, Button, Navbar, Nav, Image } from "react-bootstrap";
import { useHistory } from "react-router";
import { BellDot, User, Ticket, Dices, Pickaxe, Rocket, ChevronDown, Menu, X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { gsap } from "gsap";
import "react-toastify/dist/ReactToastify.css";

import { Path } from "../../../../static/Constants";
import { networkID } from "../../../../static/utils";
import web3 from "../../../Service/web3";
import { colors } from "../../../../theme/colors";
import { typography } from "../../../../theme/typography";

import Notification from "../../notification/Notification";
import { CommonDropdown, CommonDropdownItem } from "../../CommonDropdown/CommonDropdown";
import CommonButton from "../../ui/inputs/CommonButton.js";

// Extracted Animations
import { 
  animateNavHover, 
  animateNavLeave, 
  initNavbarScrollAnimation,
  animateMobileNavBgIn,
  animateMobileNavBgOut,
  initMobileMenuAnimation
} from "../animations";

const NavBar = () => {
  const [user, setUser] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileDropdown, setExpandedMobileDropdown] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  const navbarRef = useRef(null);
  const logoImageRef = useRef(null);
  const logoTextRef = useRef(null);
  const mobileMenuTl = useRef(null);
  const underlineRef = useRef(null);
  
  let history = useHistory();
  const apiUrl = process.env.REACT_APP_API_KEY;

  // =====================================================
  // METAMASK / WEB3 (STRICTLY UNTOUCHED)
  // =====================================================
  useEffect(() => {
    if (window.ethereum) {
      window.web3 = web3;
      window.ethereum.on("accountsChanged", () => {
        getuser();
      });
      getuser();
    }
  }, []);

  const getuser = async () => {
    let account = await web3.eth.getAccounts();
    if (account.length !== 0) {
      setUser(account[0]);
    }
  };

  const handleConnectWallet = () => {
    if (window.ethereum) {
      window.ethereum.enable();

      web3.eth.net.getId().then((netId) => {
        if (netId == networkID) {
        } else {
          setIsModalOpen(true);
          setModalMessage(`Please Connect to Main net (Network Id ${networkID})`);
        }
      });
    } else {
      setIsModalOpen(true);
      setModalMessage(
        "Please install an Ethereum-compatible browser or extension like MetaMask to use this dApp!"
      );
    }
  };

  // =====================================================
  // DATA FETCHING & WEBSOCKETS
  // =====================================================
  const fetchData = async () => {
    try {
      const getNotificationsFromLocalStorage = () => {
        const savedNotifications = localStorage.getItem("notifications");
        if (savedNotifications) {
          setNotifications(JSON.parse(savedNotifications));
        }
      };
      let account = await web3.eth.getAccounts();
      const response = await fetch(`${apiUrl}/notifications?limit=5&skip=0`, {
        method: "GET",
        headers: { Authorization: account[0] },
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        localStorage.setItem("notifications", JSON.stringify(data));
      } else {
        console.error("Error fetching notifications:", response.statusText);
        getNotificationsFromLocalStorage();
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    const fetchWSData = async () => {
      try {
        let account = await web3.eth.getAccounts();
        let ws = new WebSocket(`${apiUrl}/ws`);
        ws.onopen = () => {
          ws.send(JSON.stringify({ id: account[0] }));
          ws.onmessage = (e) => {
            let resp = JSON.parse(e.data);
            const storedWSNotifications = localStorage.getItem("wsnotifications") || "[]";
            const wsnotifications = JSON.parse(storedWSNotifications);
            
            if (!wsnotifications.includes(resp.title)) {
              wsnotifications.push(resp.title);
              localStorage.setItem("wsnotifications", JSON.stringify(wsnotifications));
              toast.success(resp.title, {
                position: "top-right",
                hideProgressBar: false,
                onClose: () => {
                  const updatedWSNotifications = wsnotifications.filter((item) => item !== resp.title);
                  localStorage.setItem("wsnotifications", JSON.stringify(updatedWSNotifications));
                },
              });
            }
          };
        };
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchWSData();
  }, []);

  // =====================================================
  // UI HANDLERS & GSAP ANIMATIONS
  // =====================================================
  
  // Sticky Navbar Scroll
  useEffect(() => {
    return initNavbarScrollAnimation(navbarRef, logoImageRef, logoTextRef);
  }, []);

  const handleNavHover = (e) => animateNavHover(underlineRef, e.currentTarget);
  const handleNavLeave = () => animateNavLeave(underlineRef);

  // Lock body scroll on mobile menu
  useEffect(() => {
    const handleScrollLock = () => {
      if (isMobileMenuOpen && window.innerWidth < 768) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
      } else {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };

    handleScrollLock();
    window.addEventListener("resize", handleScrollLock);
    return () => {
      window.removeEventListener("resize", handleScrollLock);
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isMobileMenuOpen]);

  // Handle Resize Breakpoints & Timeline Recreation
  useEffect(() => {
    let ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setExpandedMobileDropdown(null);
      }
      
      const wasMobile = mobileMenuTl.current !== null;
      const isMobile = window.innerWidth < 768;
      
      // Only rebuild GSAP context when crossing the breakpoint
      if (wasMobile !== isMobile) {
        if (!isMobile) {
          gsap.set(".navbarcollapse", { clearProps: "opacity,visibility" });
          gsap.set(".mobile-nav-item", { clearProps: "opacity,transform" });
        }
        ctx?.revert();
        ctx = initMobileMenuAnimation(navbarRef, mobileMenuTl);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      mobileMenuTl.current?.kill();
      ctx?.revert();
    };
  }, []);

  // Trigger Mobile Menu Timeline
  useEffect(() => {
    if (window.innerWidth < 768 && mobileMenuTl.current) {
      if (isMobileMenuOpen) {
        mobileMenuTl.current.timeScale(1).play();
      } else {
        mobileMenuTl.current.timeScale(1).reverse();
      }
    }
  }, [isMobileMenuOpen]);


  return (
    <Navbar 
      ref={navbarRef} 
      fixed="top" 
      collapseOnSelect 
      expand="md" 
      className="autobet-navbar"
      expanded={isMobileMenuOpen}
      onToggle={(expanded) => setIsMobileMenuOpen(expanded)}
      style={{
        '--nav-color': colors.secondary.contrastText,
        '--nav-hover-color': colors.primary.light,
        '--nav-font-family': typography.fontFamily,
        '--nav-font-size': typography.body1.fontSize,
        '--nav-font-weight': typography.fontWeightMedium,
      }}
    >
      <Nav className="mr-auto">
        <Nav.Link href={Path.root} className="Logo ml-sm-3 ml-0">
          <Image
            ref={logoImageRef}
            onClick={() => history.push("/")}
            src={require("../../../../static/Images/logo.png")}
            style={{ width: 50, height: 50, cursor: "pointer", marginRight: "5px" }}
          />
          <span className="autobet-logo-text">
            <span className="autobet-logo-auto">Auto</span>
            <span className="autobet-logo-bet">BET</span>
          </span>
        </Nav.Link>
      </Nav>
      
      <div className="d-flex align-items-center" style={{ gap: '20px' }}>
        <div className="d-block d-md-none position-relative">
          <div className="bell-icon" onClick={() => { history.push("/notifications"); fetchData(); }} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <BellDot size={20} color={colors.secondary.contrastText} />
          </div>
        </div>

        {user !== "" ? (
          <div className="d-block d-md-none" onClick={() => history.push("/profile")} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
            <User size={24} color={colors.secondary.contrastText} />
            <span style={{ fontSize: "9px", fontWeight: 600, color: colors.secondary.contrastText, letterSpacing: "0.03em", lineHeight: 1, opacity: 0.85, whiteSpace: "nowrap" }}>
              {`${user.substring(0, 2)}....${user.substring(user.length - 4, user.length)}`}
            </span>
          </div>
        ) : (
          <div className="d-block d-md-none">
            <CommonButton onClick={() => handleConnectWallet()} variant="contained" size="small" sx={{ borderRadius: "8px", px: 2, py: 0.5 }}>
              Connect
            </CommonButton>
          </div>
        )}
        
        <Navbar.Toggle aria-controls="basic-navbar-nav" className="custom-toggler border-0 shadow-none p-0" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={28} color={colors.secondary.contrastText} /> : <Menu size={28} color={colors.secondary.contrastText} />}
        </Navbar.Toggle>
      </div>

      <div className={`navbar-collapse collapse justify-content-end navbarcollapse ${isMobileMenuOpen ? "show" : ""}`}>
        <Nav style={{ position: "relative", alignItems: "center", gap: "1.25rem" }} onMouseLeave={() => { handleNavLeave(); }}>
          <div 
            ref={underlineRef} 
            style={{ position: "absolute", bottom: "0px", height: "2px", backgroundColor: colors.primary.light, opacity: 0, pointerEvents: "none", zIndex: 10, left: 0, width: 0, borderRadius: "2px" }} 
          />
          
          {/* Explosive Lotteries Dropdown */}
          <Nav.Link 
            as="div"
            className={`hovernav mobile-nav-item mobile-dropdown-trigger ${expandedMobileDropdown === 'lotteries' ? 'expanded' : ''}`} 
            onMouseEnter={(e) => { 
              if (window.innerWidth >= 768) {
                handleNavLeave();
                setIsDropdownOpen(true);
              } else {
                animateMobileNavBgIn(e);
              }
            }}
            onTouchStart={animateMobileNavBgIn}
            onMouseLeave={(e) => { 
              if (window.innerWidth >= 768) {
                setIsDropdownOpen(false);
              } else {
                animateMobileNavBgOut(e);
              }
            }}
            onTouchEnd={animateMobileNavBgOut}
            onClick={(e) => {
              if (e.target.closest && e.target.closest('.mobile-lottery-submenu')) return;
              e.preventDefault();
              e.stopPropagation();
              if (window.innerWidth < 768) setExpandedMobileDropdown(prev => prev === 'lotteries' ? null : 'lotteries');
            }}
            style={{ position: "relative" }}
          >
            <div className="mobile-hover-bg d-md-none" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', opacity: 0, backgroundColor: colors.primary.light, pointerEvents: 'none', zIndex: 0 }} />
            <div className="nav-link-content d-flex justify-content-center align-items-center gap-2" style={{ position: 'relative', zIndex: 1, color: colors.neutral.white }}>
              Explosive Lotteries
              <ChevronDown size={16} strokeWidth={2.5} className={`dropdown-chevron ${expandedMobileDropdown === 'lotteries' ? 'rotated' : ''}`} style={{ marginTop: "2px", transition: 'transform 0.3s ease' }} />
            </div>

            {/* Desktop dropdown */}
            <div className="d-none d-md-block">
              <CommonDropdown isVisible={isDropdownOpen} align="left">
                <CommonDropdownItem icon={<Ticket size={20}/>} title="Autobet MRL" subtitle="Main raffle lottery" to={Path.alllottery} />
                <CommonDropdownItem icon={<Dices size={20}/>} title="Autobet Revolver" subtitle="Spin and win" to={Path.Spinnerlottery} />
                <CommonDropdownItem icon={<Pickaxe size={20}/>} title="Autobet Mine" subtitle="Mine for rewards" to={Path.allminelottery} />
                <CommonDropdownItem icon={<Rocket size={20}/>} title="Autobet Missile" subtitle="Fast paced lottery" to={Path.allmissilelottery} />
              </CommonDropdown>
            </div>

            {/* Mobile submenu */}
            <div className={`mobile-lottery-submenu d-md-none${expandedMobileDropdown === 'lotteries' ? ' mobile-lottery-submenu--open' : ''}`} style={{ position: 'relative', zIndex: 1 }}>
              <div className="mobile-lottery-inner">
                <a href={Path.alllottery} className="mobile-lottery-item">Autobet MRL</a>
                <a href={Path.Spinnerlottery} className="mobile-lottery-item">Autobet Revolver</a>
                <a href={Path.allminelottery} className="mobile-lottery-item">Autobet Mine</a>
                <a href={Path.allmissilelottery} className="mobile-lottery-item">Autobet Missile</a>
              </div>
            </div>
          </Nav.Link>

          {/* Standard Navigation Links Iterated */}
          {[
            { name: "Gift Cards", path: Path.allgiftcards },
            { name: "Results", path: Path.result },
            { name: "Lottery Creator", path: Path.org },
            { name: "News", path: Path.news },
            { name: "Help", path: Path.help },
            { name: "About Us", path: Path.about },
          ].map((link) => (
            <Nav.Link
              key={link.name}
              onMouseEnter={(e) => { handleNavHover(e); animateMobileNavBgIn(e); }}
              onTouchStart={animateMobileNavBgIn}
              onMouseLeave={animateMobileNavBgOut}
              onTouchEnd={animateMobileNavBgOut}
              className={`hovernav mobile-nav-item ${window.location.pathname.includes(link.path) ? "activenav" : ""}`}
              href={link.path}
              style={{ position: 'relative' }}
            >
              <div className="mobile-hover-bg d-md-none" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', opacity: 0, backgroundColor: colors.primary.light, pointerEvents: 'none', zIndex: 0 }} />
              <div className="nav-link-content" style={{ position: 'relative', zIndex: 1, color: colors.neutral.white }}>{link.name}</div>
            </Nav.Link>
          ))}

          <div className="d-none d-md-block">
            <Nav.Link className="hovernav d-flex" onMouseEnter={() => { handleNavLeave(); setIsNotifOpen(true); fetchData(); }} onMouseLeave={() => setIsNotifOpen(false)} style={{ position: "relative" }}>
              <div className="bell-icon" onClick={() => { history.push("/notifications"); fetchData(); }} style={{ display: "flex", alignItems: "center" }}>
                <BellDot size={18} style={{ marginBottom: "2px" }} />
              </div>
              <CommonDropdown isVisible={isNotifOpen} align="right">
                <Notification notifications={notifications} />
              </CommonDropdown>
            </Nav.Link>
          </div>

          <div className="d-none d-md-block">
            {user === "" && (
              <Nav.Link onMouseEnter={handleNavLeave} style={{ padding: "0 10px" }}>
                <CommonButton onClick={() => handleConnectWallet()} variant="contained" size="medium" sx={{ borderRadius: "8px", px: 3, py: 1 }}>
                  Connect
                </CommonButton>
              </Nav.Link>
            )}
          </div>
          
          <div className="d-none d-md-block">
            {user !== "" && (
              <Nav.Link href={Path.profile} className="accNAdd d-flex align-items-center hovernav" onMouseEnter={handleNavLeave} style={{ gap: "0.5rem" }}>
                <span className="AccountAddress" style={{ color: "inherit", fontSize: "inherit", fontWeight: "inherit" }}>
                  {`${user.substring(0, 2)}.... ${user.substring(user.length - 4, user.length)}`}
                </span>
                <User size={22} color="currentColor" style={{ cursor: "pointer" }} />
              </Nav.Link>
            )}
          </div>
        </Nav>
      </div>

      <Modal className="PopupStyle" show={isModalOpen} onHide={() => setIsModalOpen(false)} onClose={() => setIsModalOpen(false)}>
        <Modal.Body>
          <Container>
            <Row>
              <Col lg={12}>
                <div className="message-text">{modalMessage}</div>
                <p className="link-textt" style={{ marginTop: "16px" }}>
                  <a href="/help" style={{ color: colors.primary.main, textDecoration: "none", fontWeight: 500 }}>
                    Get Help
                  </a>
                </p>
              </Col>
              <Col xs={12}>
                <div className="btn-div">
                  <Button variant="primary" className="submit-btn" onClick={() => setIsModalOpen(false)}>
                    OK
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
      <ToastContainer autoClose={5000} />
    </Navbar>
  );
};

export default NavBar;